import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Search, X } from 'lucide-react';
import {
  TrialTrackerIndication,
  UNCATEGORIZED_CANCER_TYPE,
  groupIndicationsByCancerType,
  matchesIndicationQuery,
} from '../../utils/trialTrackerTimeline';

export const ALL_INDICATIONS_LABEL = 'All indications (grouped)';

interface IndicationComboProps {
  indications: TrialTrackerIndication[];
  /** Single-select mode (Assets view). Ignored when `multi` is true. */
  value?: string;
  onChange?: (value: string) => void;
  /** Multi-select mode (Trial Tracker). Empty array = all indications. */
  multi?: boolean;
  values?: string[];
  onChangeMulti?: (values: string[]) => void;
  emptyId?: string;
  emptyLabel?: string;
}

/**
 * Always-visible indication browser grouped by SoC cancer_type.
 * Supports single-select (default) or multi-select (`multi`).
 */
const IndicationCombo: React.FC<IndicationComboProps> = ({
  indications,
  value = 'all',
  onChange,
  multi = false,
  values = [],
  onChangeMulti,
  emptyId = 'all',
  emptyLabel = ALL_INDICATIONS_LABEL,
}) => {
  const [query, setQuery] = useState('');
  const [cancerFilter, setCancerFilter] = useState<string | 'all'>('all');
  const listRef = useRef<HTMLDivElement | null>(null);

  const selectedIds = useMemo(() => {
    if (multi) return new Set(values.filter((v) => v && v !== emptyId));
    return value && value !== emptyId ? new Set([value]) : new Set<string>();
  }, [multi, values, value, emptyId]);

  const selectedRows = useMemo(
    () => indications.filter((i) => selectedIds.has(String(i.id))),
    [indications, selectedIds],
  );

  const isAll = selectedIds.size === 0;

  const cancerTypes = useMemo(() => {
    const set = new Set<string>();
    indications.forEach((i) => set.add((i.cancer_type || '').trim() || UNCATEGORIZED_CANCER_TYPE));
    return Array.from(set).sort((a, b) => {
      if (a === UNCATEGORIZED_CANCER_TYPE) return 1;
      if (b === UNCATEGORIZED_CANCER_TYPE) return -1;
      return a.localeCompare(b);
    });
  }, [indications]);

  const filteredIndications = useMemo(() => {
    let list = indications;
    if (cancerFilter !== 'all') {
      list = list.filter((i) => ((i.cancer_type || '').trim() || UNCATEGORIZED_CANCER_TYPE) === cancerFilter);
    }
    const q = query.trim();
    if (!q) return list;
    return list.filter((i) => matchesIndicationQuery(i.name, q) || matchesIndicationQuery(i.cancer_type || '', q));
  }, [indications, query, cancerFilter]);

  const groups = useMemo(() => groupIndicationsByCancerType(filteredIndications), [filteredIndications]);

  useEffect(() => {
    if (!listRef.current || selectedIds.size === 0) return;
    const first = [...selectedIds][0];
    const el = listRef.current.querySelector(`[data-indication-id="${first}"]`);
    if (el && 'scrollIntoView' in el) {
      (el as HTMLElement).scrollIntoView({ block: 'nearest' });
    }
  }, [selectedIds]);

  const clearSelection = () => {
    if (multi) onChangeMulti?.([]);
    else onChange?.(emptyId);
  };

  const toggleId = (id: string) => {
    if (multi) {
      const next = new Set(selectedIds);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      onChangeMulti?.(Array.from(next));
      return;
    }
    onChange?.(id === value ? emptyId : id);
  };

  const summaryLabel = isAll
    ? emptyLabel
    : selectedRows.length === 1
      ? selectedRows[0].name
      : `${selectedRows.length} indications selected`;

  return (
    <div className="mb-3.5 flex flex-col gap-1.5">
      <button
        type="button"
        onClick={clearSelection}
        className={`w-full text-left rounded-md border px-2.5 py-2 transition-colors ${
          isAll
            ? 'bg-tracker-accent border-tracker-accent text-white'
            : 'bg-white border-tracker-line text-tracker-ink hover:border-tracker-cardBorderUnread'
        }`}
      >
        <div className="text-[10px] font-bold tracking-wide uppercase opacity-80">
          {multi ? 'Selected (click to clear)' : 'Selected'}
        </div>
        <div className="text-[12px] font-semibold leading-snug mt-0.5">{summaryLabel}</div>
        {!multi && selectedRows[0]?.cancer_type && (
          <div className={`text-[10px] mt-0.5 ${isAll ? 'text-white/80' : 'text-tracker-muted'}`}>
            {selectedRows[0].cancer_type}
          </div>
        )}
      </button>

      {multi && selectedRows.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {selectedRows.map((ind) => (
            <span
              key={ind.id}
              className="inline-flex items-center gap-1 text-[10.5px] font-semibold px-2 py-0.5 rounded-full bg-tracker-statusBg text-tracker-accent border border-tracker-cardBorderUnread"
            >
              {ind.name}
              <X
                size={11}
                className="opacity-70 cursor-pointer hover:opacity-100"
                onClick={(e) => {
                  e.stopPropagation();
                  toggleId(String(ind.id));
                }}
              />
            </span>
          ))}
        </div>
      )}

      <div className="flex items-center gap-1.5 bg-white border border-tracker-line rounded-md px-2 py-1.5 focus-within:border-tracker-accent">
        <Search size={12} className="text-tracker-muted flex-none" />
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={multi ? 'Filter & multi-select…' : 'Filter indications…'}
          spellCheck={false}
          autoComplete="off"
          className="flex-1 min-w-0 text-[12px] text-tracker-ink outline-none bg-transparent"
        />
        {query && (
          <X size={12} className="text-tracker-muted cursor-pointer hover:text-tracker-ink flex-none" onClick={() => setQuery('')} />
        )}
      </div>

      <div className="flex flex-wrap gap-1 max-h-20 overflow-y-auto pr-0.5">
        <span
          onClick={() => setCancerFilter('all')}
          className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border cursor-pointer ${
            cancerFilter === 'all'
              ? 'bg-tracker-accent border-tracker-accent text-white'
              : 'bg-white border-tracker-line text-tracker-muted hover:border-tracker-cardBorderUnread'
          }`}
        >
          All types
        </span>
        {cancerTypes.map((ct) => (
          <span
            key={ct}
            onClick={() => setCancerFilter(ct)}
            title={ct}
            className={`text-[10px] font-medium px-2 py-0.5 rounded-full border cursor-pointer max-w-[9.5rem] truncate ${
              cancerFilter === ct
                ? 'bg-tracker-accent border-tracker-accent text-white'
                : 'bg-white border-tracker-line text-tracker-ink hover:border-tracker-cardBorderUnread'
            }`}
          >
            {ct}
          </span>
        ))}
      </div>

      <div
        ref={listRef}
        className="bg-white border border-tracker-line rounded-lg overflow-y-auto max-h-[22rem] shadow-sm"
      >
        {groups.length === 0 && (
          <div className="px-3 py-6 text-center text-[11.5px] text-tracker-muted">No matching indications</div>
        )}
        {groups.map((group) => (
          <div key={group.cancerType}>
            <div className="sticky top-0 z-[1] px-2.5 py-1.5 bg-tracker-sidebar/95 backdrop-blur-sm border-b border-tracker-line">
              <div className="text-[10px] font-bold tracking-wide text-tracker-muted uppercase">
                {group.cancerType}
              </div>
              <div className="text-[9.5px] text-tracker-muted">
                {group.items.length} indication{group.items.length === 1 ? '' : 's'}
              </div>
            </div>
            {group.items.map((ind) => {
              const id = String(ind.id);
              const active = selectedIds.has(id);
              return (
                <button
                  key={ind.id}
                  type="button"
                  data-indication-id={id}
                  onClick={() => toggleId(id)}
                  className={`w-full text-left flex items-start gap-2 px-2.5 py-2 border-b border-tracker-line3 last:border-b-0 ${
                    active
                      ? 'bg-tracker-statusBg text-tracker-accent'
                      : 'hover:bg-tracker-bg text-tracker-ink'
                  }`}
                >
                  {multi && (
                    <span
                      className={`mt-0.5 w-3.5 h-3.5 rounded border flex-none ${
                        active ? 'bg-tracker-accent border-tracker-accent' : 'border-tracker-line bg-white'
                      }`}
                    />
                  )}
                  <span className="flex-1 min-w-0 text-[12px] font-semibold leading-snug">{ind.name}</span>
                  <span className={`flex-none text-[10px] mt-0.5 ${active ? 'text-tracker-accent/80' : 'text-tracker-muted'}`}>
                    {ind.trial_count === 0 ? '—' : ind.trial_count}
                  </span>
                </button>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
};

export default IndicationCombo;
